import { ExportOutlined } from '@ant-design/icons';
import { message, Modal } from 'antd';
import { TFunction } from 'i18next';
import { createElement } from 'react';

import { ADMIN_URL, COMPANY_URLS } from '@/constants/urls';

/**
 * metadata:
 * - feature: forms.shared-utilities
 * - purpose: centraliza helpers reutilizados en múltiples formularios para evitar duplicidad
 */

/**
 * Formatea el label de un ticket para uso en selectores.
 *
 * metadata:
 * - feature: forms.ticket-label-format
 * - purpose: elimina la repetición del mismo template literal en useTicketForm, useShipmentForm, useReceptionForm, useServiceOrderForm
 */
export const formatTicketLabel = (item: {
  id: number;
  title?: string;
  ticket_title_str?: string;
}) =>
  `${item.id} - ${item.title ? item.title : item.ticket_title_str ? item.ticket_title_str : '-'}`;

/**
 * Handler genérico para onFinishFailed de formularios Ant Design.
 * Muestra un mensaje de error si hay campos con errores de validación.
 * El callback onFailed se ejecuta siempre (antes del mensaje), útil para limpiar estado.
 *
 * metadata:
 * - feature: forms.finish-failed-handler
 * - purpose: reemplaza el bloque onFinishFailed repetido en TicketForm, ShipmentForm, ReceptionForm, ServiceOrderForm
 */
export const createFinishFailedHandler =
  (t: TFunction, onFailed?: () => void) =>
  (errorInfo: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    onFailed?.();

    if (errorInfo.errorFields.length > 0) {
      message.error(t('Por favor, revisa los campos requeridos antes de enviar el formulario'));
    }
  };

/**
 * Muestra un Modal.error de advertencia cuando faltan datos de contacto en la empresa.
 * Recibe un arreglo de warnings con su id de empresa y label descriptivo.
 *
 * metadata:
 * - feature: forms.contact-warning-modal
 * - purpose: reemplaza el Modal.error duplicado en ShipmentForm y ReceptionForm para validar contactos faltantes
 *
 * @returns true si se mostró el modal (hay warnings), false si todo está OK
 */
export const showContactWarningModal = (
  warnings: { companyId: number | string; label: string }[],
  t: TFunction,
): boolean => {
  if (warnings.length === 0) {
    return false;
  }

  Modal.error({
    title: t('Faltan datos de contacto'),
    content: createElement(
      'div',
      { className: 'FormContactWarnModal' },
      `${t('Faltan datos de contacto')}. ${t('Por favor, revise')}`,
      createElement(
        'ul',
        null,
        ...warnings.map(w =>
          createElement(
            'li',
            { key: String(w.companyId) },
            createElement(
              'a',
              {
                className: 'FormContactWarnLink',
                href: `${ADMIN_URL}${COMPANY_URLS.ADMIN_EMPRESA}/${w.companyId}`,
                target: '_blank',
                rel: 'noopener noreferrer',
              },
              w.label,
              ' ',
              createElement(ExportOutlined, { className: 'FormContactWarnLinkIcon' }),
            ),
          ),
        ),
      ),
    ),
    okText: t('Aceptar'),
    maskClosable: true,
  });

  return true;
};

/**
 * Aplana los valores iniciales de un formulario, extrayendo .id de objetos anidados.
 *
 * metadata:
 * - feature: forms.flatten-initial-values
 * - purpose: centraliza la transformación repetida en ShipmentForm y ReceptionForm de objetos anidados a IDs planos
 */
export const flattenObjectIds = (values?: Record<string, any>): Record<string, any> | undefined => {
  if (!values) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      value && typeof value === 'object' && 'id' in value ? value.id : value,
    ]),
  );
};
